import { useEffect } from 'react';
import { validateSchemas, logSchemaValidation, type SchemaObject } from './schemaValidator';

// ─── Типы Schema.org ───────────────────────────────────────────────────────

export interface SchemaService {
  '@context': 'https://schema.org';
  '@type': 'Service';
  name: string;
  description?: string;
  provider: SchemaPerson | SchemaOrganization;
  areaServed?: string[] | SchemaPlace | SchemaPlace[];
  serviceType?: string;
  offers?: SchemaOffer | { '@type': 'OfferCatalog'; name: string; itemListElement: SchemaOffer[] };
  url?: string;
}

export interface SchemaPerson {
  '@type': 'Person';
  name: string;
  description?: string;
  url?: string;
  jobTitle?: string;
  image?: string;
  sameAs?: string[];
  knowsAbout?: string[];
  worksFor?: SchemaOrganization;
  address?: SchemaPostalAddress;
}

export interface SchemaOrganization {
  '@type': 'Organization';
  name: string;
  url?: string;
  logo?: string;
  description?: string;
  address?: SchemaPostalAddress;
  geo?: SchemaGeoCoordinates;
  areaServed?: string[];
  sameAs?: string[];
  founder?: SchemaPerson;
  contactPoint?: SchemaContactPoint;
}

export interface SchemaBreadcrumbList {
  '@context': 'https://schema.org';
  '@type': 'BreadcrumbList';
  itemListElement: SchemaListItem[];
}

export interface SchemaListItem {
  '@type': 'ListItem';
  position: number;
  name: string;
  item: { '@id': string; name: string };
}

export interface SchemaFAQPage {
  '@context': 'https://schema.org';
  '@type': 'FAQPage';
  mainEntity: SchemaQuestion[];
}

export interface SchemaQuestion {
  '@type': 'Question';
  name: string;
  acceptedAnswer: { '@type': 'Answer'; text: string };
}

export interface SchemaArticle {
  '@context': 'https://schema.org';
  '@type': 'Article';
  headline: string;
  description: string;
  author: SchemaPerson;
  publisher: SchemaOrganization;
  datePublished?: string;
  dateModified?: string;
  mainEntityOfPage?: { '@type': 'WebPage'; '@id': string };
  image?: string;
  inLanguage?: string;
  about?: { '@type': 'Thing'; name: string };
}

export interface SchemaWebPage {
  '@context': 'https://schema.org';
  '@type': 'WebPage';
  name: string;
  description: string;
  url: string;
  inLanguage?: string;
  isPartOf?: { '@type': 'WebSite'; name: string; url: string };
  breadcrumb?: SchemaBreadcrumbList;
}

export interface SchemaWebSite {
  '@context': 'https://schema.org';
  '@type': 'WebSite';
  name: string;
  url: string;
}

export interface SchemaOffer {
  '@type': 'Offer';
  availability?: string;
  category?: string;
  itemOffered?: {
    '@type': 'Service' | 'Course';
    name: string;
    description?: string;
  };
}

export interface SchemaPlace {
  '@type': 'Place';
  name: string;
}

export interface SchemaPostalAddress {
  '@type': 'PostalAddress';
  addressLocality: string;
  addressCountry: string;
}

export interface SchemaGeoCoordinates {
  '@type': 'GeoCoordinates';
  latitude: string;
  longitude: string;
}

export interface SchemaContactPoint {
  '@type': 'ContactPoint';
  telephone?: string;
  contactType?: string;
}

export interface SchemaContactPage {
  '@type': 'ContactPage';
  name: string;
  description: string;
  url: string;
  mainEntity: SchemaOrganization;
}

export interface SchemaCollectionPage {
  '@type': 'CollectionPage';
  '@id': string;
  url: string;
  name: string;
  description: string;
  isPartOf: { '@type': 'WebSite'; '@id': string; name: string; url: string };
  mainEntity: {
    '@type': 'ItemList';
    name: string;
    numberOfItems: number;
    itemListElement: SchemaListItem[];
  };
}

// ─── Компонент JsonLd (вставка в head) ──────────────────────────────────────

interface JsonLdProps {
  data: object;
  /** Имя страницы для логирования валидации (только в dev) */
  pageName?: string;
}

export const JsonLd = ({ data, pageName }: JsonLdProps) => {
  useEffect(() => {
    if (import.meta.env.DEV && pageName) {
      const result = validateSchemas([data as SchemaObject]);
      logSchemaValidation(result, pageName);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);

    // Удаляем именно созданный нами тег — иначе при переходах между
    // страницами можно удалить чужую разметку или оставить дубли в <head>.
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [data, pageName]);

  return null;
};

// ─── SchemaScript — для прямого встраивания в JSX ──────────────────────────

interface SchemaScriptProps {
  data: object;
}

export const SchemaScript = ({ data }: SchemaScriptProps) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
  />
);

// ─── Валидированный SchemaScript: проверяет массив схем перед рендером ──────

interface ValidatedSchemasProps {
  schemas: SchemaObject[];
  pageName: string;
}

export const ValidatedSchemas = ({ schemas, pageName }: ValidatedSchemasProps) => {
  if (import.meta.env.DEV) {
    const result = validateSchemas(schemas);
    logSchemaValidation(result, pageName);
  }

  return (
    <>
      {schemas.map((schema, i) => (
        <SchemaScript key={i} data={schema} />
      ))}
    </>
  );
};

// ─── HomePageJsonLd ─────────────────────────────────────────────────────────

export const HomePageJsonLd = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://business-training.by';

  const organizationData: SchemaOrganization & { '@context': string; address?: SchemaPostalAddress; geo?: SchemaGeoCoordinates } = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Business Training',
    url: siteUrl,
    logo: 'https://static.readdy.ai/image/045fa1b133efd9567759baab28602a6d/61a59271a80ff4d07ef88e35462987b3.png',
    description: 'Корпоративные тренинги и обучение сотрудников в Минске, Беларуси, России и Казахстане',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Минск',
      addressCountry: 'BY',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '53.9006',
      longitude: '27.5590',
    },
    areaServed: ['BY', 'RU', 'KZ'],
    sameAs: ['https://t.me/a_sivko', 'https://sivko.by/'],
  };

  const faqData: SchemaFAQPage = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Какие тренинги по продажам проводятся для отдела продаж?', acceptedAnswer: { '@type': 'Answer', text: 'Мы проводим корпоративные тренинги по продажам в Минске и Беларуси, которые помогают увеличить конверсию, улучшить работу с возражениями и повысить средний чек. Программы включают практические кейсы, ролевые игры и сценарии встреч, адаптированные под специфику вашего бизнеса и уровень сотрудников. Наши тренинги по продажам также доступны для компаний в России и Казахстане.' } },
      { '@type': 'Question', name: 'Можно ли заказать корпоративный тренинг онлайн в Минске и других регионах?', acceptedAnswer: { '@type': 'Answer', text: 'Да, наши тренинги проводятся как офлайн в Минске и Беларуси, так и онлайн для удалённых команд в России, Казахстане и других странах. Онлайн-формат включает интерактивные задания, бизнес-игры и практические упражнения, что позволяет достичь таких же результатов, как при очном обучении.' } },
      { '@type': 'Question', name: 'Что такое корпоративный тренинг и чем он полезен?', acceptedAnswer: { '@type': 'Answer', text: 'Корпоративный тренинг — это обучение сотрудников, направленное на развитие ключевых навыков, повышение эффективности работы и улучшение командной коммуникации. Наши тренинги помогают руководителям, отделам продаж и командам любых направлений в Минске, Беларуси, России и Казахстане достигать конкретных бизнес-результатов.' } },
      { '@type': 'Question', name: 'Какие тренинги по переговорам вы проводите?', acceptedAnswer: { '@type': 'Answer', text: 'Мы обучаем менеджеров эффективным стратегиям переговоров, работе с метасообщениями, психологическим приёмам и техникам достижения win-win решений. Программа адаптируется под специфику вашей отрасли и уровень сотрудников. Наши тренинги по переговорам проводятся для компаний в Минске, Беларуси, России и Казахстане.' } },
      { '@type': 'Question', name: 'Что такое бизнес-игра и тимбилдинг?', acceptedAnswer: { '@type': 'Answer', text: 'Бизнес-игра — это интерактивное командное обучение, развивающее стратегическое мышление, навыки совместной работы и коммуникации. Тимбилдинг формирует сплочённость команды, улучшает атмосферу в коллективе и помогает повысить мотивацию сотрудников. Мы проводим бизнес-игры и тимбилдинги для команд в Минске, Беларуси, России и Казахстане.' } },
      { '@type': 'Question', name: 'Можно ли провести тимбилдинг для всей команды компании?', acceptedAnswer: { '@type': 'Answer', text: 'Да, мы организуем корпоративные тимбилдинги и бизнес-игры для команд любого размера. Это интерактивное обучение, которое развивает стратегическое мышление, коммуникации и сплочённость коллектива. Возможна организация как офлайн, так и онлайн.' } },
      { '@type': 'Question', name: 'Сколько стоят корпоративные тренинги?', acceptedAnswer: { '@type': 'Answer', text: 'Стоимость тренингов зависит от формата, продолжительности и выбранной программы. Мы подбираем индивидуальные решения для каждой компании, чтобы обучение было максимально эффективным и рентабельным. Цены на корпоративные тренинги в Минске, Беларуси, России и Казахстане обсуждаются индивидуально.' } },
    ],
  };

  const serviceData: SchemaService = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Корпоративные тренинги и обучение',
    name: 'Корпоративные тренинги и обучение сотрудников',
    provider: {
      '@type': 'Organization',
      name: 'Business Training',
      url: siteUrl,
    },
    areaServed: [
      { '@type': 'City', name: 'Минск' },
      { '@type': 'Country', name: 'Беларусь' },
      { '@type': 'Country', name: 'Россия' },
      { '@type': 'Country', name: 'Казахстан' },
    ],
    offers: {
      '@type': 'OfferCatalog',
      name: 'Услуги корпоративного обучения',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Course', name: 'Тренинги по продажам', description: 'Повышение конверсии, работа с возражениями, сценарии встреч для увеличения эффективности отдела продаж' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Course', name: 'Тренинги по переговорам', description: 'Эффективные стратегии переговоров, метасообщения, психологические приемы и достижение win-win решений' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Course', name: 'Тренинги по лидерству', description: 'Развитие управленческих компетенций, управление командой и мотивация для создания эффективной рабочей среды' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Тимбилдинг', description: 'Формирование сплочённой команды, улучшение атмосферы в компании и укрепление связей в коллективе' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Бизнес-игры', description: 'Интерактивное командное обучение, развивающее стратегическое мышление и коммуникации' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Course', name: 'Обучение soft skills', description: 'Эмоциональный интеллект, стресс-менеджмент, самопрезентация, тайм-менеджмент и адаптивность к изменениям' } },
      ],
    },
  };

  const webPageData: SchemaWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Корпоративные тренинги и обучение сотрудников в Минске, Беларуси, России и Казахстане',
    description: 'Корпоративные тренинги и обучение сотрудников в Минске, Беларуси, России и Казахстане. Продажи, переговоры, лидерство, soft skills. Закажите тренинг онлайн или офлайн и увеличьте эффективность команды.',
    url: siteUrl,
    inLanguage: 'ru-RU',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Business Training',
      url: siteUrl,
    },
  };

  return (
    <ValidatedSchemas
      schemas={[organizationData, faqData, serviceData, webPageData]}
      pageName="HomePage"
    />
  );
};